import type { ColumnDef } from "@tanstack/react-table"
import { Landmark } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { ClipboardButton } from "@/components/commons/clipboard-button"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { DataTable } from "@/components/ui/data-table"
import { usePermission } from "@/contexts/permission"
import { PaymentMethodNameEnum } from "@/enums"
import { formatMoney } from "@/helpers/format"
import { DateHelper } from "@/utils"

import { BankSlipBankDataDialog } from "./bank-slip-bank-data-dialog"
import { PaymentMethodCell } from "./payment-method-cell"
import { ProcessorInfoPopover } from "./processor-info-popover"
import { RefundInfo } from "./refund-info"
import { getTransactionStatusLabel } from "./transaction-status"
import { createTransactionsColumns } from "./transactions-columns"
import { UpdatedTransactionsInfo } from "./updated-transactions-info"

interface TransactionsTableProps {
  transactions: TransactionsDetailsColumnsType[]
  withFooter?: boolean
}

// Boleto e Boletopix compartilham o mesmo fluxo de consulta no banco.
const isBankSlipMethod = (method?: string) =>
  method === PaymentMethodNameEnum.BANK_SLIP ||
  method === PaymentMethodNameEnum.BOLEPIX

const getStatusVariant = (status?: string) => {
  switch (status?.toUpperCase()) {
    case "AUTHORIZED":
    case "CAPTURED":
    case "PAID":
      return "default"
    case "PENDING":
    case "PROCESSING":
      return "secondary"
    case "REFUNDED":
    case "CANCELED":
    case "FAILED":
      return "destructive"
    default:
      return "outline"
  }
}

export const TransactionsTable = ({
  transactions,
  withFooter = true,
}: TransactionsTableProps) => {
  const { t } = useTranslation()
  // Ação exclusiva de staff Pay2B (@pay2b.io).
  const { isSuperAdmin } = usePermission()

  // Id da transação com o dialog de dados do banco aberto (null = fechado).
  const [bankDataOpenId, setBankDataOpenId] = useState<
    string | null
  >(null)

  const columns = useMemo(() => {
    const baseColumns = createTransactionsColumns(t)
    if (!isSuperAdmin) return baseColumns

    // Coluna de ação Super Admin: botão de dados do boleto no banco,
    // só para transações de boleto/boletopix.
    const bankDataColumn: ColumnDef<TransactionsDetailsColumnsType> =
      {
        id: "bankData",
        header: "",
        enableHiding: false,
        cell: ({ row }) => {
          if (!isBankSlipMethod(row.original.method)) {
            return null
          }
          return (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() =>
                setBankDataOpenId(row.original.id)
              }
            >
              <Landmark className="h-4 w-4" />
              {t(
                "orders.transactionsTable.bankData.button"
              )}
            </Button>
          )
        },
      }
    return [...baseColumns, bankDataColumn]
  }, [t, isSuperAdmin])

  const total = transactions.reduce(
    (sum, tx) => sum + tx.amount,
    0
  )

  return (
    <>
      {/* Mobile: Card layout */}
      <div className="space-y-3 md:hidden">
        {transactions.map((transaction) => {
          const allRefunds = [
            ...(transaction.refundedTransactions || []),
            ...(transaction.reversedTransactions || []),
          ]
          // Valores estornados só aparecem para cartão de crédito.
          const hasRefunds =
            allRefunds.length > 0 &&
            transaction.method ===
              PaymentMethodNameEnum.CREDIT_CARD
          const refundedAmount = hasRefunds
            ? allRefunds.reduce(
                (sum, r) => sum + r.amount,
                0
              )
            : 0

          return (
            <div
              key={transaction.id}
              className="rounded-lg border bg-card p-4"
            >
              <div className="mb-3 flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-center gap-1">
                    <code className="truncate rounded bg-muted px-1.5 py-0.5 text-xs">
                      {transaction.id}
                    </code>
                    <ClipboardButton
                      text={transaction.id}
                      variant="ghost"
                      className="h-6 w-6 shrink-0"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {DateHelper.format(
                      transaction.createdAt,
                      "dd/MM/yyyy HH:mm"
                    )}
                  </p>
                </div>
                <Badge
                  variant={getStatusVariant(
                    transaction.status
                  )}
                >
                  {getTransactionStatusLabel(
                    transaction.status,
                    t
                  )}
                </Badge>
              </div>

              {transaction.payer?.fullName && (
                <div className="mb-3">
                  <p className="mb-0.5 text-xs font-medium text-muted-foreground">
                    {t("orders.transactionsTable.payer")}
                  </p>
                  <div className="flex flex-col gap-0.5">
                    <span
                      className="max-w-[280px] truncate text-sm font-medium"
                      title={
                        transaction.payer.fullName.length >
                        40
                          ? transaction.payer.fullName
                          : undefined
                      }
                    >
                      {transaction.payer.fullName}
                    </span>
                    {transaction.payer.document && (
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-muted-foreground">
                          {transaction.payer.document}
                        </span>
                        <ClipboardButton
                          text={transaction.payer.document}
                          variant="ghost"
                          className="h-6 w-6 shrink-0"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="mb-3">
                <PaymentMethodCell
                  transaction={transaction}
                  t={t}
                />
              </div>

              {transaction.processorInformation && (
                <div className="mb-3">
                  <ProcessorInfoPopover
                    processorInfo={
                      transaction.processorInformation
                    }
                    t={t}
                  />
                </div>
              )}

              <div className="flex items-end justify-between border-t pt-3">
                <div className="flex flex-wrap gap-2">
                  <RefundInfo
                    refundedTransactions={
                      transaction.refundedTransactions
                    }
                    reversedTransactions={
                      transaction.reversedTransactions
                    }
                    paymentMethod={transaction.method}
                    t={t}
                  />
                  <UpdatedTransactionsInfo
                    updatedTransactions={
                      transaction.updatedTransactions
                    }
                    t={t}
                  />
                </div>
                <div className="text-right">
                  <p className="text-lg font-semibold">
                    {formatMoney(transaction.amount / 100)}
                  </p>
                  {hasRefunds && (
                    <p className="text-xs text-orange-600">
                      -{formatMoney(refundedAmount / 100)}{" "}
                      {t(
                        "orders.transactionsTable.refunds.refunded"
                      )}
                    </p>
                  )}
                </div>
              </div>

              {isSuperAdmin &&
                isBankSlipMethod(transaction.method) && (
                  <div className="mt-3 border-t pt-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full gap-1.5"
                      onClick={() =>
                        setBankDataOpenId(transaction.id)
                      }
                    >
                      <Landmark className="h-4 w-4" />
                      {t(
                        "orders.transactionsTable.bankData.button"
                      )}
                    </Button>
                  </div>
                )}
            </div>
          )
        })}
        {withFooter && transactions.length > 0 && (
          <div className="flex items-center justify-between rounded-lg border bg-muted/50 p-4">
            <span className="font-medium">Total</span>
            <span className="text-lg font-semibold text-primary">
              {formatMoney(total / 100)}
            </span>
          </div>
        )}
      </div>

      {/* Desktop: Table layout */}
      <div className="hidden md:block">
        <DataTable
          columns={columns}
          data={transactions}
          withFooter={withFooter}
        />
      </div>

      {/* Dialog de dados do boleto no banco (Super Admin). Instância
          única controlada pelo id da transação selecionada. */}
      {isSuperAdmin && (
        <BankSlipBankDataDialog
          paymentTransactionId={bankDataOpenId ?? ""}
          open={bankDataOpenId !== null}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) setBankDataOpenId(null)
          }}
        />
      )}
    </>
  )
}
